import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  HStack,
  Box,
  Text,
  Alert,
  AlertIcon,
  useColorModeValue,
  Divider,
  Link,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Checkbox,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { ButtonEditorData, KMDeckButton } from '../../types/KMDeck';

interface ButtonEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (button: KMDeckButton) => void;
  button: KMDeckButton | null;
  position: string; // R1C1 format
  columns: number;
  _remoteTriggerUrl?: string;
}

const ButtonEditor: React.FC<ButtonEditorProps> = ({
  isOpen,
  onClose,
  onSave,
  button,
  position,
  columns,
  _remoteTriggerUrl
}) => {
  const [formData, setFormData] = useState<ButtonEditorData>({
    title: '',
    backgroundColor: '#1a1a1a',
    backgroundImageUrl: '',
    macroUuid: '',
    parameter: '',
    fontFamily: 'Arial, sans-serif',
    fontSize: 18,
    fontColor: '#ffffff',
    hideTitle: false,
    isToggle: false,
    toggleState1: {
      title: '',
      backgroundColor: '#1a1a1a',
      backgroundImageUrl: '',
      macroUuid: '',
      parameter: '',
      fontFamily: 'Arial, sans-serif',
      fontSize: 18,
      fontColor: '#ffffff',
      hideTitle: false
    },
    toggleState2: {
      title: '',
      backgroundColor: '#1a1a1a',
      backgroundImageUrl: '',
      macroUuid: '',
      parameter: '',
      fontFamily: 'Arial, sans-serif',
      fontSize: 18,
      fontColor: '#ffffff',
      hideTitle: false
    }
  });
  const [error, setError] = useState<string | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingToggleTitle1, setIsEditingToggleTitle1] = useState(false);
  const [isEditingToggleTitle2, setIsEditingToggleTitle2] = useState(false);
  const [showComicSansWarning, setShowComicSansWarning] = useState(false);
  const [copiedSettings, setCopiedSettings] = useState<ButtonEditorData | null>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    if (button) {
      const effectiveButton = button;
      
      const fontFamily = effectiveButton.fontFamily || 'Arial, sans-serif';
      setFormData({
        title: effectiveButton.title,
        backgroundColor: effectiveButton.backgroundColor,
        backgroundImageUrl: effectiveButton.backgroundImageUrl || '',
        macroUuid: effectiveButton.macroUuid,
        parameter: effectiveButton.parameter || '',
        fontFamily: fontFamily,
        fontSize: effectiveButton.fontSize || 18,
        fontColor: effectiveButton.fontColor || '#ffffff',
        hideTitle: effectiveButton.hideTitle || false,
        isToggle: effectiveButton.isToggle || false,
        toggleState1: effectiveButton.toggleState1 || {
          title: effectiveButton.title,
          backgroundColor: effectiveButton.backgroundColor,
          backgroundImageUrl: effectiveButton.backgroundImageUrl || '',
          macroUuid: effectiveButton.macroUuid,
          parameter: effectiveButton.parameter || '',
          fontFamily: fontFamily,
          fontSize: effectiveButton.fontSize || 18,
          fontColor: effectiveButton.fontColor || '#ffffff',
          hideTitle: effectiveButton.hideTitle || false
        },
        toggleState2: effectiveButton.toggleState2 || {
          title: effectiveButton.toggleState1?.title || effectiveButton.title,
          backgroundColor: effectiveButton.toggleState1?.backgroundColor || effectiveButton.backgroundColor,
          backgroundImageUrl: effectiveButton.toggleState1?.backgroundImageUrl || effectiveButton.backgroundImageUrl || '',
          macroUuid: effectiveButton.toggleState1?.macroUuid || effectiveButton.macroUuid,
          parameter: effectiveButton.toggleState1?.parameter || effectiveButton.parameter || '',
          fontFamily: effectiveButton.toggleState1?.fontFamily || fontFamily,
          fontSize: effectiveButton.toggleState1?.fontSize || effectiveButton.fontSize || 18,
          fontColor: effectiveButton.toggleState1?.fontColor || effectiveButton.fontColor || '#ffffff',
          hideTitle: effectiveButton.toggleState1?.hideTitle || effectiveButton.hideTitle || false
        }
      });
    } else {
      // Start with empty macro UUID for new button
      setFormData({
        title: '',
        backgroundColor: '#1a1a1a',
        backgroundImageUrl: '',
        macroUuid: '',
        parameter: '',
        fontFamily: 'Arial, sans-serif',
        fontSize: 18,
        fontColor: '#ffffff',
        hideTitle: false,
        isToggle: false,
        toggleState1: {
          title: '',
          backgroundColor: '#1a1a1a',
          backgroundImageUrl: '',
          macroUuid: '',
          parameter: '',
          fontFamily: 'Arial, sans-serif',
          fontSize: 18,
          fontColor: '#ffffff',
          hideTitle: false
        },
        toggleState2: {
          title: '',
          backgroundColor: '#1a1a1a',
          backgroundImageUrl: '',
          macroUuid: '',
          parameter: '',
          fontFamily: 'Arial, sans-serif',
          fontSize: 18,
          fontColor: '#ffffff',
          hideTitle: false
        }
      });
    }
    setError(null);
    setActiveTabIndex(0); // Reset to first tab when modal opens
  }, [button, isOpen]);

  const handleResetClick = () => {
    setFormData({
      title: '',
      backgroundColor: '#1a1a1a',
      backgroundImageUrl: '',
      macroUuid: '',
      parameter: '',
      fontFamily: 'Arial, sans-serif',
      fontSize: 18,
      fontColor: '#ffffff',
      hideTitle: false,
      isToggle: false,
      toggleState1: {
        title: '',
        backgroundColor: '#1a1a1a',
        backgroundImageUrl: '',
        macroUuid: '',
        parameter: '',
        fontFamily: 'Arial, sans-serif',
        fontSize: 18,
        fontColor: '#ffffff',
        hideTitle: false
      },
      toggleState2: {
        title: '',
        backgroundColor: '#1a1a1a',
        backgroundImageUrl: '',
        macroUuid: '',
        parameter: '',
        fontFamily: 'Arial, sans-serif',
        fontSize: 18,
        fontColor: '#ffffff',
        hideTitle: false
      }
    });
    setError(null);
    setIsEditingTitle(false);
    setIsEditingToggleTitle1(false);
    setIsEditingToggleTitle2(false);
  };

  const handleInputChange = (field: keyof ButtonEditorData, value: string | number | boolean) => {
    // Easter egg: Comic Sans warning and prevention
    if (field === 'fontFamily' && value === "'Comic Sans MS', cursive") {
      setShowComicSansWarning(true);
      setTimeout(() => {
        setShowComicSansWarning(false);
      }, 1000);
      // Don't actually change the font - keep the previous selection
      return;
    }
    
    
    // Special handling for toggle checkbox
    if (field === 'isToggle') {
      if (value === true) {
        // When enabling toggle - copy current form settings to both toggle states
        setFormData(prev => ({
          ...prev,
          [field]: value,
          toggleState1: {
            title: prev.title,
            backgroundColor: prev.backgroundColor,
            backgroundImageUrl: prev.backgroundImageUrl || '',
            macroUuid: prev.macroUuid,
            parameter: prev.parameter || '',
            fontFamily: prev.fontFamily || 'Arial, sans-serif',
            fontSize: prev.fontSize || 18,
            fontColor: prev.fontColor || '#ffffff',
            hideTitle: prev.hideTitle || false
          },
          toggleState2: {
            title: prev.title,
            backgroundColor: prev.backgroundColor,
            backgroundImageUrl: prev.backgroundImageUrl || '',
            macroUuid: prev.macroUuid,
            parameter: prev.parameter || '',
            fontFamily: prev.fontFamily || 'Arial, sans-serif',
            fontSize: prev.fontSize || 18,
            fontColor: prev.fontColor || '#ffffff',
            hideTitle: prev.hideTitle || false
          }
        }));
      } else {
        // When disabling toggle - copy settings from the currently active tab back to main form
        setFormData(prev => {
          const activeTabState = activeTabIndex === 0 ? prev.toggleState1 : prev.toggleState2;
          return {
            ...prev,
            [field]: value as boolean,
            title: activeTabState.title,
            backgroundColor: activeTabState.backgroundColor,
            backgroundImageUrl: activeTabState.backgroundImageUrl || '',
            macroUuid: activeTabState.macroUuid,
            parameter: activeTabState.parameter || '',
            fontFamily: activeTabState.fontFamily || 'Arial, sans-serif',
            fontSize: activeTabState.fontSize || 18,
            fontColor: activeTabState.fontColor || '#ffffff',
            hideTitle: activeTabState.hideTitle || false,
            // Clear toggle states when disabling toggle
            toggleState1: {
              title: '',
              backgroundColor: '#1a1a1a',
              backgroundImageUrl: '',
              macroUuid: '',
              parameter: '',
              fontFamily: 'Arial, sans-serif',
              fontSize: 18,
              fontColor: '#ffffff',
              hideTitle: false
            },
            toggleState2: {
              title: '',
              backgroundColor: '#1a1a1a',
              backgroundImageUrl: '',
              macroUuid: '',
              parameter: '',
              fontFamily: 'Arial, sans-serif',
              fontSize: 18,
              fontColor: '#ffffff',
              hideTitle: false
            }
          };
        });
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    
    setError(null);
  };

  const handleToggleStateChange = (state: 'toggleState1' | 'toggleState2', field: keyof ButtonEditorData['toggleState1'], value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [state]: {
        ...prev[state],
        [field]: value
      }
    }));
    setError(null);
  };

  const renderToggleStateForm = (state: 'toggleState1' | 'toggleState2', stateData: ButtonEditorData['toggleState1']) => {
    const isEditingToggleTitle = state === 'toggleState1' ? isEditingToggleTitle1 : isEditingToggleTitle2;
    const setIsEditingToggleTitle = state === 'toggleState1' ? setIsEditingToggleTitle1 : setIsEditingToggleTitle2;
    
    return (
      <VStack spacing={4} align="stretch">
        {/* Button Preview for this state */}
        <Box textAlign="center">
          <Text color="gray.400" fontSize="sm" mb={2}>
            Preview - State {state === 'toggleState1' ? '1' : '2'}
          </Text>
          <Box
            w="120px"
            h="120px"
            bg={stateData.backgroundColor}
            borderRadius="lg"
            border="2px solid"
            borderColor={isEditingToggleTitle ? "blue.400" : "gray.600"}
            display="flex"
            alignItems="center"
            justifyContent="center"
            mx="auto"
            position="relative"
            overflow="hidden"
            cursor="pointer"
            _hover={{
              borderColor: isEditingToggleTitle ? "blue.400" : "blue.400",
              transform: isEditingToggleTitle ? "none" : "scale(1.02)"
            }}
            transition="all 0.2s ease"
            style={{
              backgroundImage: stateData.backgroundImageUrl 
                ? `url(${stateData.backgroundImageUrl})` 
                : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
            onClick={() => setIsEditingToggleTitle(true)}
          >
            {!stateData.hideTitle && (
              isEditingToggleTitle ? (
                <Textarea
                  value={stateData.title}
                  onChange={(e) => handleToggleStateChange(state, 'title', e.target.value)}
                  placeholder="Edit title"
                  bg="transparent"
                  border="none"
                  color={stateData.fontColor}
                  fontSize={`${stateData.fontSize}px`}
                  fontFamily={stateData.fontFamily}
                  fontWeight="medium"
                  textAlign="center"
                  textShadow="0 1px 2px rgba(0,0,0,0.8)"
                  px={2}
                  whiteSpace="pre-line"
                  lineHeight="1.2"
                  resize="none"
                  h="auto"
                  minH="0"
                  w="100%"
                  _focus={{
                    outline: "none",
                    boxShadow: "none"
                  }}
                  _placeholder={{
                    color: "rgba(255,255,255,0.7)",
                    textAlign: "center"
                  }}
                  onBlur={() => setIsEditingToggleTitle(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setIsEditingToggleTitle(false);
                    }
                  }}
                  autoFocus
                />
              ) : (
                <Text
                  color={stateData.fontColor}
                  fontSize={`${stateData.fontSize}px`}
                  fontFamily={stateData.fontFamily}
                  fontWeight="medium"
                  textAlign="center"
                  textShadow="0 1px 2px rgba(0,0,0,0.8)"
                  px={2}
                  whiteSpace="pre-line"
                  lineHeight="1.2"
                  pointerEvents="none"
                >
                  {stateData.title || 'Edit title'}
                </Text>
              )
            )}
          </Box>
        </Box>

      <Divider />

      {/* Form Fields for this state */}
      {/* Checkboxes Row */}
      <HStack spacing={8} align="start">
        <FormControl>
          <Checkbox
            isChecked={stateData.hideTitle}
            onChange={(e) => handleToggleStateChange(state, 'hideTitle', e.target.checked)}
            colorScheme="blue"
            color="white"
          >
            Hide title
          </Checkbox>
        </FormControl>

        <FormControl>
          <Checkbox
            isChecked={formData.isToggle}
            onChange={(e) => handleInputChange('isToggle', e.target.checked)}
            colorScheme="blue"
            color="white"
          >
            Toggle
          </Checkbox>
        </FormControl>
      </HStack>

      <HStack spacing={4}>
        <FormControl flex={1.2}>
          <Select
            value={stateData.fontFamily}
            onChange={(e) => handleToggleStateChange(state, 'fontFamily', e.target.value)}
            bg="gray.700"
            borderColor="gray.600"
            color="gray.100"
            placeholder="Select font family"
            _focus={{
              borderColor: "blue.400",
              boxShadow: "0 0 0 1px #3182ce"
            }}
          >
            <option value="Arial, sans-serif">Arial</option>
            <option value="Helvetica, sans-serif">Helvetica</option>
            <option value="'Open Sans', sans-serif">Open Sans</option>
            <option value="'Roboto', sans-serif">Roboto</option>
            <option value="'Lato', sans-serif">Lato</option>
            <option value="'Montserrat', sans-serif">Montserrat</option>
            <option value="'Poppins', sans-serif">Poppins</option>
            <option value="'Source Sans Pro', sans-serif">Source Sans Pro</option>
            <option value="'Inter', sans-serif">Inter</option>
            <option value="'Nunito', sans-serif">Nunito</option>
            <option value="'Ubuntu', sans-serif">Ubuntu</option>
            <option value="'Times New Roman', serif">Times New Roman</option>
            <option value="Georgia, serif">Georgia</option>
            <option value="'Playfair Display', serif">Playfair Display</option>
            <option value="'Merriweather', serif">Merriweather</option>
            <option value="'Courier New', monospace">Courier New</option>
            <option value="'Fira Code', monospace">Fira Code</option>
            <option value="'JetBrains Mono', monospace">JetBrains Mono</option>
            <option value="Verdana, sans-serif">Verdana</option>
            <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
            <option value="Impact, sans-serif">Impact</option>
            <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
          </Select>
        </FormControl>

        <FormControl flex={0.6}>
          <NumberInput
            value={stateData.fontSize}
            onChange={(valueString, valueNumber) => handleToggleStateChange(state, 'fontSize', valueNumber)}
            min={8}
            max={48}
            bg="gray.700"
            borderColor="gray.600"
          >
            <NumberInputField
              color="gray.100"
              placeholder="Size"
              _focus={{
                borderColor: "blue.400",
                boxShadow: "0 0 0 1px #3182ce"
              }}
            />
            <NumberInputStepper>
              <NumberIncrementStepper color="gray.300" />
              <NumberDecrementStepper color="gray.300" />
            </NumberInputStepper>
          </NumberInput>
        </FormControl>

        <FormControl flex={1.2}>
          <HStack>
            <Input
              type="color"
              value={stateData.fontColor}
              onChange={(e) => handleToggleStateChange(state, 'fontColor', e.target.value)}
              w="60px"
              h="40px"
              p={1}
              bg="gray.700"
              borderColor="gray.600"
              _focus={{
                borderColor: "blue.400",
                boxShadow: "0 0 0 1px #3182ce"
              }}
            />
            <Input
              value={stateData.fontColor}
              onChange={(e) => handleToggleStateChange(state, 'fontColor', e.target.value)}
              placeholder="Font color (hex)"
              bg="gray.700"
              borderColor="gray.600"
              color="gray.100"
              fontSize="sm"
              _focus={{
                borderColor: "blue.400",
                boxShadow: "0 0 0 1px #3182ce"
              }}
            />
          </HStack>
        </FormControl>
      </HStack>

      <FormControl>
        <HStack>
          <Text color="gray.300" fontSize="sm" fontWeight="medium" minW="120px">
            Background colour
          </Text>
          <Input
            type="color"
            value={stateData.backgroundColor}
            onChange={(e) => handleToggleStateChange(state, 'backgroundColor', e.target.value)}
            w="60px"
            h="40px"
            p={1}
            bg="gray.700"
            borderColor="gray.600"
            _focus={{
              borderColor: "blue.400",
              boxShadow: "0 0 0 1px #3182ce"
            }}
          />
          <Input
            value={stateData.backgroundColor}
            onChange={(e) => handleToggleStateChange(state, 'backgroundColor', e.target.value)}
            placeholder="Hex code"
            bg="gray.700"
            borderColor="gray.600"
            color="gray.100"
            w="120px"
            _focus={{
              borderColor: "blue.400",
              boxShadow: "0 0 0 1px #3182ce"
            }}
          />
        </HStack>
      </FormControl>

      <FormControl>
        <Input
          value={stateData.backgroundImageUrl}
          onChange={(e) => handleToggleStateChange(state, 'backgroundImageUrl', e.target.value)}
          placeholder="Background image URL"
          bg="gray.700"
          borderColor="gray.600"
          color="gray.100"
          _focus={{
            borderColor: "blue.400",
            boxShadow: "0 0 0 1px #3182ce"
          }}
        />
      </FormControl>

      <Divider />

      <Box>
        <VStack spacing={3} align="stretch">

          <FormControl>
            <FormLabel color="gray.200" fontSize="sm" fontWeight="medium">
              Macro UUID
            </FormLabel>
            <Input
              value={stateData.macroUuid}
              onChange={(e) => handleToggleStateChange(state, 'macroUuid', e.target.value)}
              placeholder="Macro UUID from Keyboard Maestro"
              bg="gray.700"
              borderColor="gray.600"
              color="gray.100"
              fontSize="sm"
              _focus={{
                borderColor: "blue.400",
                boxShadow: "0 0 0 1px #3182ce"
              }}
            />
            <Text color="gray.400" fontSize="xs" mt={1}>
              Get this from the macro&apos;s Remote Trigger settings
            </Text>
          </FormControl>

          <FormControl>
            <FormLabel color="gray.200" fontSize="sm" fontWeight="medium">
              Parameter (optional)
            </FormLabel>
            <Input
              value={stateData.parameter}
              onChange={(e) => handleToggleStateChange(state, 'parameter', e.target.value)}
              placeholder="Parameter for %TriggerValue%"
              bg="gray.700"
              borderColor="gray.600"
              color="gray.100"
              fontSize="sm"
              _focus={{
                borderColor: "blue.400",
                boxShadow: "0 0 0 1px #3182ce"
              }}
            />
          </FormControl>
        </VStack>
      </Box>
    </VStack>
    );
  };

  const handleCopy = () => {
    setCopiedSettings({ ...formData });
  };

  const handlePaste = () => {
    if (copiedSettings) {
      setFormData({ ...copiedSettings });
    }
  };

  const handleSave = () => {
    const buttonData: KMDeckButton = {
      id: button?.id || `button-${Date.now()}`,
      title: formData.title,
      backgroundColor: formData.backgroundColor,
      backgroundImageUrl: formData.backgroundImageUrl || undefined,
      macroUuid: formData.macroUuid,
      parameter: formData.parameter || undefined,
      fontFamily: formData.fontFamily,
      fontSize: formData.fontSize,
      fontColor: formData.fontColor,
      hideTitle: formData.hideTitle,
      isToggle: formData.isToggle,
      toggleState: formData.isToggle ? (button?.toggleState ?? false) : undefined,
      toggleState1: formData.isToggle ? formData.toggleState1 : undefined,
      toggleState2: formData.isToggle ? formData.toggleState2 : undefined
    };

    onSave(buttonData);
    onClose();
  };



  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered closeOnOverlayClick={false}>
      <ModalOverlay bg="blackAlpha.600" />
      
      {/* Comic Sans Easter Egg Warning */}
      {showComicSansWarning && (
        <Box
          position="fixed"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          zIndex={9999}
          bg="yellow.400"
          color="black"
          p={8}
          borderRadius="xl"
          boxShadow="0 0 50px rgba(255, 255, 0, 0.8)"
          textAlign="center"
          animation="pulse 0.5s ease-in-out"
          fontFamily="'Comic Sans MS', cursive"
          fontSize="3xl"
          fontWeight="bold"
          textShadow="1px 1px 2px rgba(0,0,0,0.3)"
        >
          Comic Sans prohibited! 🚫
        </Box>
      )}
      
      <ModalContent bg={bgColor} maxW="md">
        <ModalHeader color="gray.100" borderBottom="1px solid" borderColor={borderColor}>
          Configure Button {position}
        </ModalHeader>
        <ModalCloseButton color="gray.300" />
        
        <ModalBody py={6}>
          <VStack spacing={2} align="stretch">
            {/* Reset Button - Top Right, Copy/Paste Buttons - Top Left (Regular Mode Only) */}
            {!formData.isToggle && (
              <Box position="relative" w="full" mb={2}>
                {/* Copy/Paste Buttons - Top Left */}
                <VStack spacing={1} align="stretch" position="absolute" top={0} left={0} zIndex={10}>
                  <Button
                    onClick={handleCopy}
                    size="sm"
                    variant="outline"
                    color="gray.300"
                    borderColor="gray.500"
                    opacity={0.8}
                    _hover={{ 
                      opacity: 1,
                      bg: "gray.600",
                      color: "white",
                      borderColor: "gray.400"
                    }}
                    _active={{
                      bg: "gray.700"
                    }}
                    minW="60px"
                  >
                    Copy
                  </Button>
                  <Button
                    onClick={handlePaste}
                    size="sm"
                    variant="outline"
                    color="gray.300"
                    borderColor="gray.500"
                    opacity={0.8}
                    _hover={{ 
                      opacity: 1,
                      bg: "gray.600",
                      color: "white",
                      borderColor: "gray.400"
                    }}
                    _active={{
                      bg: "gray.700"
                    }}
                    minW="60px"
                    visibility={copiedSettings ? "visible" : "hidden"}
                  >
                    Paste
                  </Button>
                </VStack>

                {/* Reset Button - Top Right */}
                <Button
                  onClick={handleResetClick}
                  size="sm"
                  variant="outline"
                  color="gray.300"
                  borderColor="gray.500"
                  position="absolute"
                  top={0}
                  right={0}
                  zIndex={10}
                  opacity={0.8}
                  _hover={{ 
                    opacity: 1,
                    bg: "gray.600",
                    color: "white",
                    borderColor: "gray.400"
                  }}
                  _active={{
                    bg: "gray.700"
                  }}
                >
                  Reset
                </Button>
              </Box>
            )}


            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            )}




            {formData.isToggle ? (
              /* Tabbed Interface for Toggle States */
              <Box position="relative">
                <Tabs variant="enclosed" colorScheme="blue" onChange={(index) => setActiveTabIndex(index)}>
                  <TabList>
                    <Tab>1</Tab>
                    <Tab>2</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel px={0}>
                      {renderToggleStateForm('toggleState1', formData.toggleState1)}
                    </TabPanel>
                    <TabPanel px={0}>
                      {renderToggleStateForm('toggleState2', formData.toggleState2)}
                    </TabPanel>
                  </TabPanels>
                </Tabs>
                
                {/* Copy/Paste Buttons - Positioned relative to tab content */}
                <VStack spacing={1} align="stretch" position="absolute" top="60px" left="0" zIndex={10}>
                  <Button
                    onClick={handleCopy}
                    size="sm"
                    variant="outline"
                    color="gray.300"
                    borderColor="gray.500"
                    opacity={0.8}
                    _hover={{ 
                      opacity: 1,
                      bg: "gray.600",
                      color: "white",
                      borderColor: "gray.400"
                    }}
                    _active={{
                      bg: "gray.700"
                    }}
                    minW="60px"
                  >
                    Copy
                  </Button>
                  <Button
                    onClick={handlePaste}
                    size="sm"
                    variant="outline"
                    color="gray.300"
                    borderColor="gray.500"
                    opacity={0.8}
                    _hover={{ 
                      opacity: 1,
                      bg: "gray.600",
                      color: "white",
                      borderColor: "gray.400"
                    }}
                    _active={{
                      bg: "gray.700"
                    }}
                    minW="60px"
                    visibility={copiedSettings ? "visible" : "hidden"}
                  >
                    Paste
                  </Button>
                </VStack>

                {/* Reset Button - Positioned relative to tab content */}
                <Button
                  onClick={handleResetClick}
                  size="sm"
                  variant="outline"
                  color="gray.300"
                  borderColor="gray.500"
                  position="absolute"
                  top="60px"
                  right="0"
                  zIndex={10}
                  opacity={0.8}
                  _hover={{ 
                    opacity: 1,
                    bg: "gray.600",
                    color: "white",
                    borderColor: "gray.400"
                  }}
                  _active={{
                    bg: "gray.700"
                  }}
                >
                  Reset
                </Button>
              </Box>
            ) : (
              /* Regular Single State Form */
              <>
                {/* Button Preview */}
                <Box textAlign="center" mt={-6}>
                  <Box
                    w="120px"
                    h="120px"
                    bg={formData.backgroundColor}
                    borderRadius="lg"
                    border="2px solid"
                    borderColor={isEditingTitle ? "blue.400" : "gray.600"}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mx="auto"
                    position="relative"
                    overflow="hidden"
                    cursor="pointer"
                    _hover={{
                      borderColor: isEditingTitle ? "blue.400" : "blue.400",
                      transform: isEditingTitle ? "none" : "scale(1.02)"
                    }}
                    transition="all 0.2s ease"
                    style={{
                      backgroundImage: formData.backgroundImageUrl 
                        ? `url(${formData.backgroundImageUrl})` 
                        : undefined,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                    onClick={() => setIsEditingTitle(true)}
                  >
                    {!formData.hideTitle && (
                      isEditingTitle ? (
                        <Textarea
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          placeholder="Edit title"
                          bg="transparent"
                          border="none"
                          color={formData.fontColor}
                          fontSize={`${formData.fontSize}px`}
                          fontFamily={formData.fontFamily}
                          fontWeight="medium"
                          textAlign="center"
                          textShadow="0 1px 2px rgba(0,0,0,0.8)"
                          px={2}
                          whiteSpace="pre-line"
                          lineHeight="1.2"
                          resize="none"
                          h="auto"
                          minH="0"
                          w="100%"
                          _focus={{
                            outline: "none",
                            boxShadow: "none"
                          }}
                          _placeholder={{
                            color: "rgba(255,255,255,0.7)",
                            textAlign: "center"
                          }}
                          onBlur={() => setIsEditingTitle(false)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setIsEditingTitle(false);
                            }
                          }}
                          autoFocus
                        />
                      ) : (
                        <Text
                          color={formData.fontColor}
                          fontSize={`${formData.fontSize}px`}
                          fontFamily={formData.fontFamily}
                          fontWeight="medium"
                          textAlign="center"
                          textShadow="0 1px 2px rgba(0,0,0,0.8)"
                          px={2}
                          whiteSpace="pre-line"
                          lineHeight="1.2"
                          pointerEvents="none"
                        >
                          {formData.title || 'Edit title'}
                        </Text>
                      )
                    )}
                  </Box>
                </Box>

                <Divider />

                {/* Form Fields */}

                {/* Checkboxes Row */}
                <HStack spacing={8} align="start">
                  <FormControl>
                    <Checkbox
                      isChecked={formData.hideTitle}
                      onChange={(e) => handleInputChange('hideTitle', e.target.checked)}
                      colorScheme="blue"
                      color="white"
                    >
                      Hide title
                    </Checkbox>
                  </FormControl>

                  <FormControl>
                    <Checkbox
                      isChecked={formData.isToggle}
                      onChange={(e) => handleInputChange('isToggle', e.target.checked)}
                      colorScheme="blue"
                      color="white"
                    >
                      Toggle
                    </Checkbox>
                  </FormControl>
                </HStack>

                <HStack spacing={4}>
                  <FormControl flex={1.2}>
                    <Select
                      value={formData.fontFamily}
                      onChange={(e) => handleInputChange('fontFamily', e.target.value)}
                      bg="gray.700"
                      borderColor="gray.600"
                      color="gray.100"
                      placeholder="Select font family"
                      _focus={{
                        borderColor: "blue.400",
                        boxShadow: "0 0 0 1px #3182ce"
                      }}
                    >
                      <option value="Arial, sans-serif">Arial</option>
                      <option value="Helvetica, sans-serif">Helvetica</option>
                      <option value="'Open Sans', sans-serif">Open Sans</option>
                      <option value="'Roboto', sans-serif">Roboto</option>
                      <option value="'Lato', sans-serif">Lato</option>
                      <option value="'Montserrat', sans-serif">Montserrat</option>
                      <option value="'Poppins', sans-serif">Poppins</option>
                      <option value="'Source Sans Pro', sans-serif">Source Sans Pro</option>
                      <option value="'Inter', sans-serif">Inter</option>
                      <option value="'Nunito', sans-serif">Nunito</option>
                      <option value="'Ubuntu', sans-serif">Ubuntu</option>
                      <option value="'Times New Roman', serif">Times New Roman</option>
                      <option value="Georgia, serif">Georgia</option>
                      <option value="'Playfair Display', serif">Playfair Display</option>
                      <option value="'Merriweather', serif">Merriweather</option>
                      <option value="'Courier New', monospace">Courier New</option>
                      <option value="'Fira Code', monospace">Fira Code</option>
                      <option value="'JetBrains Mono', monospace">JetBrains Mono</option>
                      <option value="Verdana, sans-serif">Verdana</option>
                      <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                      <option value="Impact, sans-serif">Impact</option>
                      <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                    </Select>
                  </FormControl>

                  <FormControl flex={0.6}>
                    <NumberInput
                      value={formData.fontSize}
                      onChange={(valueString, valueNumber) => handleInputChange('fontSize', valueNumber)}
                      min={8}
                      max={48}
                      bg="gray.700"
                      borderColor="gray.600"
                    >
                      <NumberInputField
                        color="gray.100"
                        placeholder="Size"
                        _focus={{
                          borderColor: "blue.400",
                          boxShadow: "0 0 0 1px #3182ce"
                        }}
                      />
                      <NumberInputStepper>
                        <NumberIncrementStepper color="gray.300" />
                        <NumberDecrementStepper color="gray.300" />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl flex={1.2}>
                    <HStack>
                      <Input
                        type="color"
                        value={formData.fontColor}
                        onChange={(e) => handleInputChange('fontColor', e.target.value)}
                        w="60px"
                        h="40px"
                        p={1}
                        bg="gray.700"
                        borderColor="gray.600"
                        _focus={{
                          borderColor: "blue.400",
                          boxShadow: "0 0 0 1px #3182ce"
                        }}
                      />
                      <Input
                        value={formData.fontColor}
                        onChange={(e) => handleInputChange('fontColor', e.target.value)}
                        placeholder="Font color (hex)"
                        bg="gray.700"
                        borderColor="gray.600"
                        color="gray.100"
                        fontSize="sm"
                        _focus={{
                          borderColor: "blue.400",
                          boxShadow: "0 0 0 1px #3182ce"
                        }}
                      />
                    </HStack>
                  </FormControl>
                </HStack>

                <FormControl>
                  <HStack>
                    <Text color="gray.300" fontSize="sm" fontWeight="medium" minW="120px">
                      Background colour
                    </Text>
                    <Input
                      type="color"
                      value={formData.backgroundColor}
                      onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                      w="60px"
                      h="40px"
                      p={1}
                      bg="gray.700"
                      borderColor="gray.600"
                      _focus={{
                        borderColor: "blue.400",
                        boxShadow: "0 0 0 1px #3182ce"
                      }}
                    />
                    <Input
                      value={formData.backgroundColor}
                      onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                      placeholder="Hex code"
                      bg="gray.700"
                      borderColor="gray.600"
                      color="gray.100"
                      w="120px"
                      _focus={{
                        borderColor: "blue.400",
                        boxShadow: "0 0 0 1px #3182ce"
                      }}
                    />
                  </HStack>
                </FormControl>

                <FormControl>
                  <Input
                    value={formData.backgroundImageUrl}
                    onChange={(e) => handleInputChange('backgroundImageUrl', e.target.value)}
                    placeholder="Background image URL"
                    bg="gray.700"
                    borderColor="gray.600"
                    color="gray.100"
                    _focus={{
                      borderColor: "blue.400",
                      boxShadow: "0 0 0 1px #3182ce"
                    }}
                  />
                </FormControl>

                <Divider />

                <Box>
                  <VStack spacing={2} align="stretch">

                    <FormControl>
                      <FormLabel color="gray.200" fontSize="sm" fontWeight="medium">
                        Macro UUID
                      </FormLabel>
                      <Input
                        value={formData.macroUuid}
                        onChange={(e) => handleInputChange('macroUuid', e.target.value)}
                        placeholder="Macro UUID from Keyboard Maestro"
                        bg="gray.700"
                        borderColor="gray.600"
                        color="gray.100"
                        fontSize="sm"
                        _focus={{
                          borderColor: "blue.400",
                          boxShadow: "0 0 0 1px #3182ce"
                        }}
                      />
                      <Text color="gray.400" fontSize="xs" mt={1}>
                        Get this from the macro&apos;s Remote Trigger settings
                      </Text>
                    </FormControl>

                    <FormControl>
                      <FormLabel color="gray.200" fontSize="sm" fontWeight="medium">
                        Parameter (optional)
                      </FormLabel>
                      <Input
                        value={formData.parameter}
                        onChange={(e) => handleInputChange('parameter', e.target.value)}
                        placeholder="Parameter for %TriggerValue%"
                        bg="gray.700"
                        borderColor="gray.600"
                        color="gray.100"
                        fontSize="sm"
                        _focus={{
                          borderColor: "blue.400",
                          boxShadow: "0 0 0 1px #3182ce"
                        }}
                      />
                    </FormControl>
                  </VStack>
                </Box>
              </>
            )}

            <Box textAlign="center">
              <Link
                href="https://wiki.keyboardmaestro.com/trigger/Remote"
                isExternal
                color="blue.400"
                fontSize="xs"
              >
                Learn about Keyboard Maestro Remote Triggers <ExternalLinkIcon mx="2px" />
              </Link>
            </Box>

            <HStack spacing={3} pt={2}>
              <Button
                onClick={onClose}
                variant="outline"
                colorScheme="gray"
                color="gray.300"
                borderColor="gray.600"
                _hover={{ bg: "gray.700" }}
                flex={1}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                colorScheme="blue"
                bg="blue.500"
                _hover={{ bg: "blue.600" }}
                _active={{ bg: "blue.700" }}
                flex={1}
              >
                Save Button
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>

    </Modal>
  );
};

export default ButtonEditor;
